"use client"

/**
 * Shared avatar upload control for widget-family settings panels.
 *
 * Renders the 40×40 avatar preview (or an Upload placeholder) and a button
 * that opens a hidden file picker. On pick it POSTs the file as FormData to
 * `uploadPath` together with `channelId`; the response `{ url }` is piped
 * to the caller through `onChange` so the parent's `config.avatarUrl` stays
 * the single source of truth.
 */

import { useRef, useState } from "react"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Loader2, Upload, X } from "lucide-react"
import { toast } from "sonner"
import { useAuth } from "@/hooks/auth/use-auth"
import { useTranslations } from "next-intl"


interface AvatarUploadControlProps {
  avatarUrl?: string | null
  channelId: string
  uploadPath: string
  canManage: boolean
  onChange: (url: string) => void
  /**
   * Called with an error string on failure and `null` on upload start /
   * success — matches the `setError` pattern callers use so a stale
   * error alert from a previous attempt doesn't linger after a retry.
   */
  onError?: (message: string | null) => void
}

export function AvatarUploadControl({
  avatarUrl,
  channelId,
  uploadPath,
  canManage,
  onChange,
  onError,
}: AvatarUploadControlProps) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsAvatarUploadControl")

  const { fetchWithAuth } = useAuth()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    onError?.(null)
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("channelId", channelId)

      const response = await fetchWithAuth(uploadPath, { method: "POST", body: formData })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        const msg = data.error || "Failed to upload avatar"
        onError?.(msg)
        toast.error(msg)
        return
      }

      const { url } = await response.json()
      onChange(url)
      onError?.(null)
      toast.success(tUi("avatarUploaded"))
    } catch {
      const msg = "Failed to upload avatar"
      onError?.(msg)
      toast.error(msg)
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ""
    }
  }

  const handleRemove = () => onChange("")

  return (
    <div className="flex items-center gap-3">
      {avatarUrl ? (
        <div className="relative">
          <Image
            src={avatarUrl}
            alt={tUi("widgetAvatar")}
            width={40}
            height={40}
            className="w-10 h-10 rounded-full object-cover border"
            unoptimized
          />
          {canManage && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ) : (
        <div className="w-10 h-10 rounded-full bg-gray-100 border flex items-center justify-center">
          <Upload className="w-4 h-4 text-gray-400" />
        </div>
      )}
      <div className="flex-1">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          onChange={handleUpload}
          className="hidden"
          disabled={!canManage || uploading}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={!canManage || uploading}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          <span key={avatarUrl ? tUi("change") : tUi("upload")}>{avatarUrl ? tUi("change") : tUi("upload")}</span>
        </Button>
        <p className="text-xs text-gray-500 mt-1"><span key={tUi("pNGJPEGWebPOrSVGMax2MB")}>{tUi("pNGJPEGWebPOrSVGMax2MB")}</span></p>
      </div>
    </div>
  )
}
